import type { Router } from '@aikoa/api'
import type { inferRouterOutputs } from '@trpc/server'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { View } from 'react-native'
import { useAlert } from '~/components/GlobalAlert'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type AppRouterOutputs = inferRouterOutputs<Router>
type FeedbackWorkflowRun = AppRouterOutputs['admin']['listFeedbackWorkflowRuns']['runs'][number]

function shouldAllowRestart(run: FeedbackWorkflowRun): boolean {
  if ((run.failedCount ?? 0) > 0) {
    return true
  }

  const normalizedStatus = run.status.toLowerCase()

  return (
    normalizedStatus === 'running' ||
    normalizedStatus === 'pending' ||
    normalizedStatus === 'queued' ||
    normalizedStatus === 'failed' ||
    normalizedStatus === 'canceled' ||
    normalizedStatus === 'cancelled'
  )
}

function formatDate(dateValue: string | null): string {
  if (!dateValue) {
    return '-'
  }

  const parsedDate = new Date(dateValue)

  if (Number.isNaN(parsedDate.getTime())) {
    return '-'
  }

  return parsedDate.toLocaleDateString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatusColor(status: string): string {
  const normalizedStatus = status.toLowerCase()

  switch (normalizedStatus) {
    case 'completed': {
      return 'bg-green-100 text-green-800'
    }
    case 'failed': {
      return 'bg-red-100 text-red-800'
    }
    case 'running': {
      return 'bg-blue-100 text-blue-800'
    }
    default: {
      return 'bg-neutral-100 text-neutral-700'
    }
  }
}

function getStatusLabel(
  status: string,
  t: ReturnType<typeof useTranslations<'admin'>>['t']
): string {
  const normalizedStatus = status.toLowerCase()

  switch (normalizedStatus) {
    case 'completed': {
      return t(($) => $.workflowRuns.statuses.completed)
    }
    case 'failed': {
      return t(($) => $.workflowRuns.statuses.failed)
    }
    case 'running': {
      return t(($) => $.workflowRuns.statuses.running)
    }
    case 'queued': {
      return t(($) => $.workflowRuns.statuses.queued)
    }
    case 'pending': {
      return t(($) => $.workflowRuns.statuses.pending)
    }
    case 'cancelled': {
      return t(($) => $.workflowRuns.statuses.cancelled)
    }
    case 'canceled': {
      return t(($) => $.workflowRuns.statuses.canceled)
    }
    default: {
      return t(($) => $.workflowRuns.statuses.unknown)
    }
  }
}

function WorkflowRunCard({
  run,
  isRestarting,
  onRestartRun,
}: {
  run: FeedbackWorkflowRun
  isRestarting: boolean
  onRestartRun: (runId: string) => void
}) {
  const { t } = useTranslations('admin')
  const runStatusLabel = getStatusLabel(run.status, t)
  const restartAllowed = shouldAllowRestart(run)

  return (
    <View className="gap-3 rounded-lg border border-neutral-200 bg-white p-4">
      <View className="flex-row items-start justify-between gap-4">
        <View className="flex-1 gap-1">
          <Text className="font-medium text-base">{run.runId}</Text>
          <Label className="text-sm text-secondary">
            {run.workflowName ?? t(($) => $.workflowRuns.unknownWorkflow)}
          </Label>
        </View>
        <View className={`rounded-md px-3 py-1.5 ${getStatusColor(run.status)}`}>
          <Text className="font-medium text-sm">{runStatusLabel}</Text>
        </View>
      </View>

      <View className="flex-row flex-wrap gap-4">
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.created)}
          </Label>
          <Label className="text-sm text-secondary">{formatDate(run.createdAt)}</Label>
        </View>
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.started)}
          </Label>
          <Label className="text-sm text-secondary">{formatDate(run.startedAt)}</Label>
        </View>
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.completed)}
          </Label>
          <Label className="text-sm text-secondary">{formatDate(run.completedAt)}</Label>
        </View>
      </View>

      <View className="flex-row flex-wrap gap-4">
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.processedCount)}
          </Label>
          <Label className="text-sm text-secondary">
            {run.processedCount === null ? '-' : String(run.processedCount)}
          </Label>
        </View>
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.succeededCount)}
          </Label>
          <Label className="text-sm text-secondary">
            {run.succeededCount === null ? '-' : String(run.succeededCount)}
          </Label>
        </View>
        <View className="gap-0.5">
          <Label className="text-xs text-neutral-400">
            {t(($) => $.workflowRuns.fields.failedCount)}
          </Label>
          <Label className="text-sm text-secondary">
            {run.failedCount === null ? '-' : String(run.failedCount)}
          </Label>
        </View>
      </View>

      <View className="gap-0.5">
        <Label className="text-xs text-neutral-400">
          {t(($) => $.workflowRuns.fields.failedIds)}
        </Label>
        <Label className="text-sm text-secondary">
          {run.failedFeedbackIds.length === 0
            ? t(($) => $.workflowRuns.noneFailed)
            : run.failedFeedbackIds.join(', ')}
        </Label>
      </View>

      {restartAllowed && (
        <Button
          variant="outline"
          size="sm"
          onPress={() => {
            onRestartRun(run.runId)
          }}
          disabled={isRestarting}>
          <ButtonLabel>
            {isRestarting ? t(($) => $.workflowRuns.restarting) : t(($) => $.workflowRuns.restart)}
          </ButtonLabel>
        </Button>
      )}
    </View>
  )
}

const DEFAULT_LIMIT = 30

export default function WorkflowRuns() {
  const { t } = useTranslations('admin')
  const { showAlert } = useAlert()
  const [limitInput, setLimitInput] = useState(String(DEFAULT_LIMIT))
  const [restartingRunId, setRestartingRunId] = useState<string | null>(null)
  const parsedLimit = Number.parseInt(limitInput, 10)
  const queryLimit =
    Number.isNaN(parsedLimit) || parsedLimit < 1 || parsedLimit > 100 ? DEFAULT_LIMIT : parsedLimit

  const { data, isLoading, error, isFetching, refetch } = useQuery(
    trpc.admin.listFeedbackWorkflowRuns.queryOptions({
      limit: queryLimit,
    })
  )

  const restartRunMutation = useMutation(
    trpc.admin.restartFeedbackWorkflowRun.mutationOptions({
      onSuccess: (result) => {
        showAlert({
          type: 'success',
          title: t(($) => $.workflowRuns.restartSuccessTitle),
          description: t(($) => $.workflowRuns.restartSuccessDescription, {
            runId: result.restartedRunId,
            count: result.itemCount,
          }),
        })

        void refetch()
      },
      onError: (mutationError) => {
        showAlert({
          type: 'error',
          title: t(($) => $.workflowRuns.restartErrorTitle),
          description: mutationError.message || t(($) => $.workflowRuns.restartErrorDescription),
        })
      },
      onMutate: (variables) => {
        setRestartingRunId(variables.runId)
      },
      onSettled: () => {
        setRestartingRunId(null)
      },
    })
  )

  const handleRestartRun = (runId: string) => {
    void restartRunMutation.mutateAsync({ runId })
  }

  const workflowRuns = data?.runs ?? []

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.workflowRuns.title)}</Heading2>
          <Body className="text-center text-neutral-600">
            {t(($) => $.workflowRuns.description)}
          </Body>
        </View>

        <View className="gap-3">
          <Input
            value={limitInput}
            onChangeText={setLimitInput}
            placeholder={t(($) => $.workflowRuns.limitPlaceholder)}
            variant="box"
            className="bg-white"
            keyboardType="numeric"
          />

          <Button variant="outline" onPress={() => void refetch()} disabled={isFetching}>
            <ButtonLabel>{t(($) => $.workflowRuns.refresh)}</ButtonLabel>
          </Button>
        </View>

        {isLoading && (
          <Body className="text-center text-neutral-500">{t(($) => $.workflowRuns.loading)}</Body>
        )}

        {!!error && (
          <Body className="text-center text-red-600">{t(($) => $.workflowRuns.loadError)}</Body>
        )}

        {!isLoading && !error && workflowRuns.length === 0 && (
          <Body className="text-center text-neutral-500">{t(($) => $.workflowRuns.none)}</Body>
        )}

        {workflowRuns.length > 0 && (
          <View className="gap-3">
            {workflowRuns.map((workflowRun) => (
              <WorkflowRunCard
                key={workflowRun.runId}
                run={workflowRun}
                isRestarting={restartingRunId === workflowRun.runId}
                onRestartRun={handleRestartRun}
              />
            ))}
          </View>
        )}
      </ContentBox>
    </ScreenView>
  )
}
